/**
 * Request Throttling and Retry Utility
 * Prevents ERR_INSUFFICIENT_RESOURCES by controlling concurrent requests
 */

// Config
const DEFAULT_CONCURRENT_LIMIT = 3;
const DEFAULT_RETRY_ATTEMPTS = 3;
const DEFAULT_RETRY_DELAY = 1000; // 1 second
const MAX_RETRY_DELAY = 8000; // 8 seconds

// EMERGENCY STOP FLAG
window.REQUEST_THROTTLE_EMERGENCY_STOP = false;

class RequestThrottler {
  constructor() {
    this.queue = [];
    this.active = 0;
    this.concurrentLimit = DEFAULT_CONCURRENT_LIMIT;
    this.cache = new Map();
    this.cacheTimeout = 5 * 60 * 1000; // 5 minutes
    this.totalRequests = 0;
    this.requestHistory = [];
    this.emergencyAlertShown = false;

    // Check for emergency stop
    this.checkEmergencyStop();
  }

  checkEmergencyStop() {
    try {
      if (typeof window === 'undefined' || typeof localStorage === 'undefined') {
        window.REQUEST_THROTTLE_EMERGENCY_STOP = false;
        return;
      }

      const forceReload = localStorage.getItem('force_reload');
      if (!forceReload) {
        window.REQUEST_THROTTLE_EMERGENCY_STOP = false;
        this.emergencyAlertShown = false;
        return;
      }

      const reloadTime = parseInt(forceReload, 10);
      const currentTime = Date.now();
      const withinCooldown = Number.isFinite(reloadTime) && currentTime - reloadTime < 30000;

      if (withinCooldown) {
        window.REQUEST_THROTTLE_EMERGENCY_STOP = true;
        if (!this.emergencyAlertShown) {
          console.warn('[RequestThrottler] EMERGENCY STOP ACTIVE - Clear browser cache to continue');
          alert('CACHE CLEARING REQUIRED!\n\nPlease clear your browser cache:\n1. Press Ctrl+Shift+R (Windows) or Cmd+Shift+R (Mac)\n2. OR open DevTools (F12) → Application → Clear Storage\n\nThis app is preventing requests to fix ERR_INSUFFICIENT_RESOURCES errors.');
          this.emergencyAlertShown = true;
        }
      } else {
        window.REQUEST_THROTTLE_EMERGENCY_STOP = false;
        this.emergencyAlertShown = false;
        localStorage.removeItem('force_reload');
      }
    } catch (err) {
      console.warn('[RequestThrottler] Failed to evaluate emergency stop state', err);
      window.REQUEST_THROTTLE_EMERGENCY_STOP = false;
    }
  }

  /**
   * Execute a request with throttling and retry logic
   */
  async execute(key, requestFn, options = {}) {
    // Re-evaluate emergency stop on each request to avoid stale flags
    this.checkEmergencyStop();

    const {
      retries = DEFAULT_RETRY_ATTEMPTS,
      retryDelay = DEFAULT_RETRY_DELAY,
      useCache = true,
      cacheTimeout = this.cacheTimeout
    } = options;

    // EMERGENCY STOP CHECK
    if (window.REQUEST_THROTTLE_EMERGENCY_STOP) {
      console.warn(`[RequestThrottler] BLOCKED: ${key} - Emergency stop active`);
      throw new Error('CACHE_CLEARING_REQUIRED: Please clear browser cache to continue');
    }

    this.totalRequests++;
    this.requestHistory.push({
      key,
      timestamp: Date.now(),
      total: this.totalRequests
    });

    // Check cache first
    const cacheKey = `cache_${key}`;
    if (useCache && this.cache.has(cacheKey)) {
      const cached = this.cache.get(cacheKey);
      if (Date.now() - cached.timestamp < cacheTimeout) {
        console.log(`[RequestThrottler] ✅ Using cached result for: ${key}`);
        return cached.data;
      }
    }

    // Add to queue
    return new Promise((resolve, reject) => {
      this.queue.push({
        key,
        requestFn,
        resolve,
        reject,
        retriesLeft: retries,
        retryDelay
      });

      this.processQueue();
    });
  }

  /**
   * Process the request queue
   */
  async processQueue() {
    // EMERGENCY STOP CHECK
    if (window.REQUEST_THROTTLE_EMERGENCY_STOP) {
      console.warn('[RequestThrottler] Emergency stop active - clearing queue');
      this.queue = [];
      return;
    }

    if (this.active >= this.concurrentLimit || this.queue.length === 0) {
      return;
    }

    this.active++;
    const item = this.queue.shift();

    try {
      console.log(`[RequestThrottler] ▶️ Executing: ${item.key} (active: ${this.active}/${this.concurrentLimit})`);
      const result = await item.requestFn();

      // Cache successful result
      const cacheKey = `cache_${item.key}`;
      this.cache.set(cacheKey, {
        data: result,
        timestamp: Date.now()
      });

      item.resolve(result);
    } catch (error) {
      console.warn(`[RequestThrottler] ❌ Error for ${item.key}:`, error.message);

      // Retry logic
      if (item.retriesLeft > 0) {
        const delay = Math.min(item.retryDelay * (DEFAULT_RETRY_ATTEMPTS - item.retriesLeft + 1), MAX_RETRY_DELAY);
        console.log(`[RequestThrottler] ⏳ Retrying ${item.key} in ${delay}ms (${item.retriesLeft} attempts left)`);

        setTimeout(() => {
          this.queue.push({
            ...item,
            retriesLeft: item.retriesLeft - 1
          });
          this.active--;
          this.processQueue();
        }, delay);
      } else {
        item.reject(error);
      }
    } finally {
      this.active--;
      this.processQueue();
    }
  }

  /**
   * Set concurrent request limit
   */
  setConcurrentLimit(limit) {
    this.concurrentLimit = Math.max(1, limit);
    console.log(`[RequestThrottler] Concurrent limit set to: ${this.concurrentLimit}`);
  }

  /**
   * Clear cache
   */
  clearCache() {
    this.cache.clear();
    console.log('[RequestThrottler] Cache cleared');
  }

  /**
   * Get cache size
   */
  getCacheSize() {
    return this.cache.size;
  }

  /**
   * Get queue status
   */
  getStatus() {
    return {
      active: this.active,
      queued: this.queue.length,
      cached: this.cache.size,
      limit: this.concurrentLimit
    };
  }
}

// Create singleton instance
const requestThrottler = new RequestThrottler();

// Utility function for supabase queries with throttling
export const throttleSupabaseQuery = async (queryFn, key, options = {}) => {
  return requestThrottler.execute(key, queryFn, options);
};

// Utility function for admin stats with batching
export const getAdminStatsThrottled = async () => {
  return requestThrottler.execute(
    'admin_stats',
    async () => {
      console.log('[RequestThrottler] Fetching admin stats...');
      const { supabase } = await import('../services/supabaseClient');

      // Use a single RPC call instead of multiple Promise.all
      // This reduces concurrent connections
      const { data, error } = await supabase.rpc('admin_get_comprehensive_stats');

      if (error) {
        throw error;
      }

      return {
        countries: data?.countries_count || 0,
        pathways: data?.pathways_count || 0,
        translations: data?.translations_count || 0,
        packages: data?.packages_count || 0,
        loanOptions: data?.loan_options_count || 0,
        wizardSessions: data?.wizard_sessions_count || 0,
        wizardSubmissions: data?.wizard_submissions_count || 0
      };
    },
    {
      retries: 2,
      retryDelay: 1500,
      useCache: true,
      cacheTimeout: 2 * 60 * 1000 // 2 minutes
    }
  );
};

export default requestThrottler;
